'use client';

import { Select } from '@/components/ui/Select';
import { useI18n } from '@/contexts/I18nContext';
import { useBrands } from './useBrands';

/**
 * Brand picker shared by the publish form (label a placement) and the
 * campaigns sidebar (filter the list). Values:
 *   ''      — no brand / all brands (see `mode`)
 *   'none'  — filter mode only: placements without a brand
 *   <id>    — a live brand
 * Renders nothing while the agency has no brands: the feature stays invisible
 * until the first brand exists.
 */
export function BrandSelect({
  value,
  onChange,
  mode,
  className,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  mode: 'label' | 'filter';
  className?: string;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  const { brands } = useBrands();
  if (!brands || brands.length === 0) return null;

  const options = [
    mode === 'filter'
      ? { value: '', label: t('brands.select.all') }
      : { value: '', label: t('brands.select.noBrand') },
    ...(mode === 'filter' ? [{ value: 'none', label: t('brands.select.noBrand') }] : []),
    ...brands.map((b) => ({ value: b.id, label: b.name })),
  ];

  return (
    <Select
      value={value}
      onChange={onChange}
      options={options}
      placeholder={t('brands.select.label')}
      className={className}
      disabled={disabled}
    />
  );
}
